import React, {useMemo, useState} from 'react';
import PropTypes, { arrayOf } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EmailTemplateDetailTab from './EmailTemplateDetailTab';
import EmailTemplatePreview from './EmailTemplatePreview';
import useDetailHandler from '../../hooks/useDetailHandler';
import { Privileged } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';
import { emailTemplateType, productType } from '../../types';
import { onActionKey } from '../../utils';
import './_styles_/EmailTemplateRow.css';
import HeaderProduct from "../auctions/HeaderProduct";

EmailTemplateRow.propTypes = {
    availableProducts: arrayOf(productType),
    activeProductType: PropTypes.string,
    emailTemplate: emailTemplateType.isRequired,
    onClone: PropTypes.func.isRequired,
};

function EmailTemplateRow(props) {
    const {
        availableProducts = [],
        activeProductType = null,
        emailTemplate,
        onClone
    } = props;
    const { t } = useTranslation();
    const { isDetailVisible, toggleDetail, onDetailDelete } = useDetailHandler(emailTemplate.id);
    const [isPreviewVisible, setPreviewVisible] = useState(false);

    const openPreview = (event) => {
        event.stopPropagation();
        setPreviewVisible(true);
    };
    const cloneTemplate = (event) => {
        event.stopPropagation();
        onClone(emailTemplate);
    };

    const product = useMemo(
        () => availableProducts.find((p) => p.productId === emailTemplate.productId) || null,
        [availableProducts, emailTemplate.productId],
    );

    return (
        <>
            <div className="rich-list-row email-template-header">
                <button
                    type="button"
                    className="email-template-header--toggle"
                    onClick={toggleDetail}
                    title={t('email-templates.toggle-detail')}
                    aria-label={t('email-templates.toggle-detail')}
                    aria-expanded={isDetailVisible}
                />
                <div className="email-template-header--values--name">{emailTemplate.name}</div>
                <HeaderProduct product={product} activeProductType={activeProductType} classNamePrefix="email-template-header--values" />
                <div className="email-template-header--values--recipients">{emailTemplate.recipients}</div>
                <div className="email-template-header--values--actions">
                    <button
                        type="button"
                        className="email-template-header--action email-template-preview-trigger"
                        onClick={openPreview}
                        onKeyUp={onActionKey(openPreview)}
                        title={t('email-templates.preview.title')}
                        aria-label={t('email-templates.preview.title')}
                    >
                        <FontAwesomeIcon icon="eye" />
                    </button>
                    <Privileged sufficientPrivileges={[Privileges.EMAIL_TEMPLATE_EDIT]}>
                        <button
                            type="button"
                            className="email-template-header--action email-template-clone-trigger"
                            onClick={cloneTemplate}
                            onKeyUp={onActionKey(cloneTemplate)}
                            title={t('email-templates.clone-email-template')}
                            aria-label={t('email-templates.clone-email-template')}
                        >
                            <FontAwesomeIcon icon="clone" />
                        </button>
                    </Privileged>
                    <button
                        type="button"
                        className="email-template-header--action email-template-header--arrow"
                        onClick={toggleDetail}
                        tabIndex={-1}
                        aria-hidden="true"
                    >
                        <FontAwesomeIcon icon={`caret-${isDetailVisible ? 'down' : 'right'}`} />
                    </button>
                </div>
            </div>
            {isDetailVisible && (
                <EmailTemplateDetailTab
                    availableProducts={availableProducts}
                    emailTemplate={emailTemplate}
                    onDetailDelete={onDetailDelete}
                />
            )}
            {isPreviewVisible && (
                <EmailTemplatePreview
                    emailTemplate={emailTemplate}
                    onClose={() => setPreviewVisible(false)}
                />
            )}
        </>
    );
}

export default EmailTemplateRow;
